import axios from 'axios'
import { ArrowLeft, Calendar, CreditCard, Mail, Phone } from 'lucide-react'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { constants } from '../Constants'
import { AlertDialogCancelSubscription } from '../components/AlertDialogs/AlertDialogCancelSubscription'
import { AlertDialogResendPassword } from '../components/AlertDialogs/AlertDialogResendPassword'
import { Avatar, Badge, Button } from '../components/ui'
import { PageLoader } from '../components/ui/Spinner/Spinner'
import { useToast } from '../components/ui/Toast/Toast'
import '../styles/shared.scss'
import './SingleUser.scss'

export const SingleUser = () => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()
  const [user, setUser] = useState(location.state?.user || null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${constants.urlLocal}getUser/${id}`)
      setUser(response.data.user)
    } catch (error) {
      console.error('Error al obtener el usuario', error)
    }
  }, [id])

  useEffect(() => {
    fetchUser().finally(() => setLoading(false))
  }, [fetchUser])

  if (loading || !user)
    return (
      <div className="loading-center">
        <PageLoader />
      </div>
    )

  const startEdit = () => {
    setEditData({
      name: user.name || '',
      lastname: user.lastname || '',
      email: user.email || '',
      phone: user.phone || '',
      dateOfBirth: user.dateOfBirth
        ? moment(user.dateOfBirth).format('YYYY-MM-DD')
        : '',
    })
    setEditMode(true)
  }

  const handleEditChange = e => {
    const { name, value } = e.target
    setEditData(prev => ({ ...prev, [name]: value }))
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      const response = await axios.put(`${constants.urlLocal}update`, {
        userId: user._id,
        ...editData,
      })
      if (response.data.ok) {
        setUser(response.data.user)
        setEditMode(false)
        toast({ title: 'Datos actualizados', status: 'success' })
      }
    } catch (error) {
      toast({
        title: 'Error al actualizar',
        description:
          error.response?.data?.message || 'Inténtalo de nuevo más tarde',
        status: 'error',
      })
    }
    setSaving(false)
  }

  const payments = [...(user.paymentHistory || [])].sort(
    (a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)
  )

  return (
    <div className="user-detail">
      <button className="user-detail__back" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} />
        Volver
      </button>

      <div className="user-detail__grid">
        <aside className="user-detail-hero">
          <Avatar
            name={`${user.name} ${user.lastname}`}
            src={user.avatar}
            size="xl"
          />
          {!editMode ? (
            <>
              <h1 className="user-detail-hero__name">
                {user.name} {user.lastname}
              </h1>
              <Badge variant={user.hasPaid ? 'success' : 'danger'} size="md">
                {user.hasPaid
                  ? 'Suscripción activa'
                  : 'Sin suscripción activa'}
              </Badge>

              <div className="user-detail-hero__contact">
                <span>
                  <Mail size={14} />
                  {user.email}
                </span>
                {user.phone && (
                  <span>
                    <Phone size={14} />
                    {user.phone}
                  </span>
                )}
                <span>
                  <Calendar size={14} />
                  {user.dateOfBirth
                    ? moment(user.dateOfBirth).format('DD/MM/YYYY')
                    : 'Sin fecha de nacimiento'}
                </span>
              </div>

              <div className="user-detail-hero__actions">
                <Button variant="secondary" size="sm" onClick={startEdit}>
                  Editar datos
                </Button>
                <AlertDialogResendPassword user={user} />
                <AlertDialogCancelSubscription
                  user={user}
                  onCancelled={setUser}
                />
              </div>
            </>
          ) : (
            <div className="user-detail-hero__edit-form">
              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input
                  className="form-input"
                  name="name"
                  value={editData.name}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido</label>
                <input
                  className="form-input"
                  name="lastname"
                  value={editData.lastname}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  name="email"
                  value={editData.email}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input
                  className="form-input"
                  name="phone"
                  value={editData.phone}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de nacimiento</label>
                <input
                  className="form-input"
                  type="date"
                  name="dateOfBirth"
                  value={editData.dateOfBirth}
                  onChange={handleEditChange}
                />
              </div>
              <div className="user-detail-hero__edit-actions">
                <Button
                  variant="ghost"
                  onClick={() => setEditMode(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveEdit}
                  loading={saving}
                >
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </aside>

        <section className="user-detail-card">
          <div className="user-detail-card__header">
            <h3 className="user-detail-card__title">
              <CreditCard size={18} />
              Historial de pagos
            </h3>
            <span className="user-detail-card__count">{payments.length}</span>
          </div>

          {payments.length === 0 ? (
            <p className="user-detail-card__empty">Sin pagos registrados</p>
          ) : (
            <ul className="payment-list">
              {payments.map((pay, idx) => (
                <li key={pay._id || idx} className="payment-list__item">
                  <div className="payment-list__month">
                    {moment(pay.paymentDate).format('MMMM YYYY')}
                  </div>
                  <div className="payment-list__meta">
                    {(pay.adminName || pay.adminLastname) && (
                      <span className="payment-list__admin">
                        {pay.adminName} {pay.adminLastname}
                      </span>
                    )}
                    {pay.paymentAmount != null && (
                      <span className="payment-list__amount">
                        {pay.paymentAmount} €
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
